import { GuideInputDateTime } from 'components/admin/input/GuideInputDateTime';
import { ImageGalleriesImageContent } from 'components/minute/sidebar/MarkerSidebar/shared/ImageGalleriesSubsection/ImageGalleriesImageContent';
import { CheckValue } from 'elements/minute/sidebar/sidebarComponents/atomic/CheckValue';
import { OptionRowValue } from 'elements/minute/sidebar/sidebarComponents/atomic/OptionRowValue';
import { ColorPalette } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { useDetailCRUD } from 'hooks/detail/useDetailCRUD';
import { isDefined } from 'libs/utils/typesafe/isDefined';
import { useContext, useEffect, useState } from 'react';
import { IVariantIconV2WorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IVariantIconV2WorldMarker;
    disableIconEdit?: boolean;
}

export const VariantIconMarkerStyleSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const detailCRUD = useDetailCRUD(ctxEdit.map.worldMapId);
    const [storyCategoryIds, setStoryCategoryIds] = useState<string[]>(null);

    const categoryColors =
        ctxEdit.map.worldMapSets
            ?.filter((e) => storyCategoryIds?.includes(e.worldMapSetId))
            .map((e) => e.color)
            ?.filter(isDefined) ?? [];

    useEffect(() => {
        if (props.marker?.storyIds?.length !== 1) {
            return;
        }
        const readStory = async () => {
            const story = await detailCRUD.readDetail(props.marker.storyIds[0]);
            const categoryIds = story.data?.moment?.categories?.map((e) => e.worldMapSetId);
            setStoryCategoryIds(categoryIds);
        };
        readStory();
    }, [props.marker.storyIds?.length]);

    return (
        <S_IconMarkerStyleSection className={props.className}>
            <SidebarSection title={Localization.STYLE} icon={{ iconName: 'brush' }}>
                <SidebarSubsection title={Localization.HIGHLIGHTED} icon={{ iconName: 'star' }}>
                    <SidebarContent>
                        <CheckValue
                            label={
                                props.marker.elementData.withStar
                                    ? Localization.YES
                                    : Localization.NO
                            }
                            value={props.marker.elementData.withStar}
                            onChange={(value) => {
                                ctxEdit.updateWorldMarkerData(props.marker.id, {
                                    withStar: value
                                });
                            }}
                        />
                    </SidebarContent>
                </SidebarSubsection>

                {!props.disableIconEdit && (
                    <SidebarSubsection title={Localization.ICON} icon={{ iconName: 'image' }}>
                        <SidebarContent>
                            <OptionRowValue
                                items={[
                                    { text: 'Image' },
                                    { text: 'From detail' }
                                    // { text: 'Event date' }
                                ]}
                                selectedIndex={(() => {
                                    if (props.marker.elementData.useStoryImage) {
                                        return 1;
                                    }
                                    if (props.marker.elementData.eventDate != null) {
                                        return 2;
                                    }
                                    return 0;
                                })()}
                                onSelected={(index) => {
                                    if (index === 0) {
                                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                                            useStoryImage: false,
                                            eventDate: null
                                        });
                                    } else if (index === 1) {
                                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                                            useStoryImage: true,
                                            eventDate: null
                                        });
                                    } else if (index === 2) {
                                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                                            useStoryImage: false,
                                            eventDate: ''
                                        });
                                    }
                                }}
                            />
                            {props.marker.elementData.eventDate != null && (
                                <S_GuideInputDateTime
                                    type="date"
                                    label={Localization.DATE}
                                    value={props.marker.elementData.eventDate}
                                    direction="horizontal"
                                    onChange={(_value) => {
                                        const value = _value?.trim();
                                        if (!value) {
                                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                                eventDate: ''
                                            });
                                        }
                                        ctxEdit.updateWorldMarkerData(props.marker.id, {
                                            eventDate: value
                                        });
                                    }}
                                />
                            )}
                            {!props.marker.elementData.useStoryImage &&
                                props.marker.elementData.eventDate == null && (
                                    <S_ImageGalleriesImageContent
                                        selectedImageUrl={props.marker.elementData.imageUrl}
                                        highlightedCategoryIds={storyCategoryIds}
                                        galleryType={'image'}
                                        onSelected={(imageUrl) => {
                                            ctxEdit.updateWorldMarkerData(props.marker.id, {
                                                imageUrl
                                            });
                                        }}
                                    />
                                )}
                        </SidebarContent>
                    </SidebarSubsection>
                )}
                <SidebarSubsection title={Localization.COLOR} icon={{ iconName: 'palette' }}>
                    <SidebarContent>
                        <ColorPalette
                            value={props.marker.elementData.color}
                            pickerAllowed
                            onChange={(color) => {
                                ctxEdit.updateWorldMarkerData(props.marker.id, {
                                    color
                                });
                            }}
                            highlightedColors={categoryColors}
                        />
                    </SidebarContent>
                </SidebarSubsection>
            </SidebarSection>
        </S_IconMarkerStyleSection>
    );
};

const S_IconMarkerStyleSection = styled.div``;

const S_GuideInputDateTime = styled(GuideInputDateTime)`
    margin-top: 20px;
`;

const S_ImageGalleriesImageContent = styled(ImageGalleriesImageContent)`
    margin-top: 10px;
`;
